<template>
  <div class="lv-upload-file-btn">
    <LvText v-if="title" h6 class="mb-10">
      {{ $t(title) }}
    </LvText>
    <div class="lv-upload-file-btn__content">
      <div v-if="type === 'text'" class="lv-upload-file-btn__text-btn" @click="$_onClickUpload()">
        <i class="icon-attachment lv-upload-file-btn__icon" />
        <LvText h6 blue class="ml-8 lv-upload-file-btn__text">{{
          btnText ? $t(btnText) : $t('附件')
        }}</LvText>
      </div>
      <div
        v-else
        class="lv-upload-file-btn__btn"
        :class="{ error: hasError, disabled: disabled }"
        @click="$_onClickUpload()"
      >
        <LoadingAnimate v-if="isLoading.file" primary />
        <i v-if="!isLoading.file" class="icon-cloud-upload lv-upload-file-btn__icon" />
        <LvText
          v-if="!isLoading.file"
          h4
          :bold="!disabled"
          :lightGray="disabled"
          :red="hasError && !disabled"
          :blue="!hasError && !disabled"
          class="ml-16"
          >{{ btnText ? $t(btnText) : $t('上傳') }}</LvText
        >
      </div>
      <input
        ref="fileInput"
        type="file"
        :multiple="multiple"
        :accept="_acceptFileType"
        class="lv-upload-file-btn__input"
        @change="$_onChangeFile()"
      />
    </div>
    <LvText
      remark
      class="mt-8 lv-upload-file-btn__remind-text"
      :class="{ 'light-gray': !error, red: error }"
    >
      {{ _remindText }}
    </LvText>
    <LvPopupChooseFile
      :acceptFileType="acceptFileType"
      :oneFile="oneFile"
      :uploadModule="uploadModule"
      :popUpActive="isActive.upload"
      :selectedFileList="selectedFileList"
      :versionMode="versionMode"
      @input="$emit('input', $event)"
      @close="$_onClosePopup('upload')"
    />
  </div>
</template>
<script>
import i18n from '@/i18n';
import H_file from '@/helpers/file';
import { extend } from 'vee-validate';
import { isHeic, heicTo } from 'heic-to';
import imageCompression from 'browser-image-compression';
import Spare_file from '@/spare/file';

extend('slash', {
  validate: value => !value.includes('/'),
  message: i18n.t('請不要輸入斜線符號("/")。'),
});

export default {
  props: {
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    title: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    btnText: {
      type: String,
    },
    saveToStorage: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'Btn',
    },
    uploadModule: {
      type: String,
    },
    oneFile: {
      type: Boolean,
      default: false,
    },
    acceptFileType: {
      type: String,
    },
    selectedFileList: {
      type: Array,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    versionMode: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      C_files: [],
      error: false,
      isActive: {
        upload: false,
      },
      fileLimit: 30 * 1024 * 1024,
      isLoading: {
        file: false,
      },
    };
  },
  computed: {
    _remindText() {
      return this.remindText ? this.$t(this.remindText) : `${this.$t('檔案大小限制')} ≤ 30 MB`;
    },
    _acceptFileType() {
      if (!this.acceptFileType) return '';
      switch (this.acceptFileType) {
        case 'image':
          return Spare_file.inputAcceptFileType.image;
        case 'pdf':
          return Spare_file.inputAcceptFileType.pdf;
        case 'xlsx':
          return Spare_file.inputAcceptFileType.xlsx;
        case 'docx':
          return Spare_file.inputAcceptFileType.docx;
        default:
          return this.acceptFileType;
      }
    },
  },
  watch: {
    value() {
      this.C_files = this.value;
    },
  },
  mounted() {
    this.$_initCFiles();
  },
  methods: {
    $_onClickUpload() {
      if (this.isLoading.file || this.disabled) return;
      if (this.saveToStorage) {
        this.$refs.fileInput.value = null;
        this.$refs.fileInput.click();
      } else {
        this.$_onOpenPopup('upload');
      }
    },
    async $_onChangeFile() {
      const files = this.$refs.fileInput.files;
      for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
        let file = files[fileIndex];
        if (file.size > this.fileLimit) {
          this.error = true;
          if (!this.remindText) alert(this.$t(`檔案尺寸超過{number}MB`, { number: '30' }));
          return;
        }
        if (file.type.startsWith('image/')) {
          this.isLoading.file = true;
          if (await isHeic(file)) file = await this.$_convertHeicToJpeg(file);
          file = await imageCompression(file, {
            maxSizeMB: 1,
            useWebWorker: true,
          });
          this.isLoading.file = false;
        }
        const selectedFile = {
          file,
          name: file.name,
          fileType: H_file.setFileExtension(file.name).slice(1),
          percent: 0,
          isLoading: false,
          isClickable: false,
        };
        if (this.multiple) this.C_files.push(selectedFile);
        else this.C_files = [selectedFile];
        this.$emit('input', this.C_files);
        this.C_files = [];
      }
    },
    async $_convertHeicToJpeg(file) {
      const dotIndex = file.name.lastIndexOf('.');
      const newName =
        dotIndex !== -1 ? file.name.substring(0, dotIndex) + '.jpg' : file.name + '.jpg';
      const heicToJpg = await heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 1,
      });
      return new File([heicToJpg], newName, { type: 'image/jpeg' });
    },
    $_initCFiles() {
      if (this.value) {
        this.value.forEach(valueItem => {
          this.C_files.push(valueItem);
        });
      } else {
        this.C_files = [];
      }
    },
    $_handleUploadProgress(progressEvent, target) {
      target.percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
    },
    $_onOpenPopup(type) {
      this.isActive[type] = true;
    },
    $_onClosePopup(type) {
      if (!this.isActive[type]) return;
      this.isActive[type] = false;
    },
  },
};
</script>
